import { CustomBlending, DstColorFactor, Mesh, OrthographicCamera, PlaneGeometry, Scene, ShaderMaterial, SrcColorFactor, type Texture, Vector2 } from 'three';
import { paperGrainTexture } from './textures';

/**
 * Post-processing, poster style: a faint paper grain and a subtle warm vignette,
 * applied as one full-screen modulate-2x pass over the finished frame (0.5 =
 * unchanged), so it costs one cheap draw.
 */
export class PaperOverlay {
  readonly scene = new Scene();
  readonly camera = new OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private mat: ShaderMaterial;
  private grain: Texture;

  constructor() {
    this.grain = paperGrainTexture();
    this.mat = new ShaderMaterial({
      depthTest: false,
      depthWrite: false,
      transparent: true,
      blending: CustomBlending,
      blendSrc: DstColorFactor,
      blendDst: SrcColorFactor,
      uniforms: {
        uGrain: { value: this.grain },
        uRes: { value: new Vector2(1, 1) },
        uGrainAmt: { value: 0.025 },
        uVig: { value: 0.22 },
      },
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
      `,
      fragmentShader: /* glsl */ `
        uniform sampler2D uGrain;
        uniform vec2 uRes;
        uniform float uGrainAmt, uVig;
        varying vec2 vUv;
        void main() {
          // Grain in screen pixels (paper doesn't move with the scene).
          vec2 guv = gl_FragCoord.xy / 420.0;
          float g = texture2D(uGrain, guv).r;
          float g2 = texture2D(uGrain, guv * 0.37 + 0.31).r;
          float grain = (g * 0.85 + g2 * 0.15) - 0.5;
          // Warm vignette: darken toward dark brown at the corners.
          vec2 p = (vUv - 0.5) * vec2(uRes.x / uRes.y, 1.0);
          float v = smoothstep(0.35, 1.05, length(p));
          vec3 warm = mix(vec3(1.0), vec3(0.8, 0.66, 0.5), v * uVig * 2.2);
          vec3 c = 0.5 * warm * (1.0 + grain * uGrainAmt * 2.0);
          gl_FragColor = vec4(c, 1.0);
        }
      `,
    });
    const quad = new Mesh(new PlaneGeometry(2, 2), this.mat);
    quad.frustumCulled = false;
    this.scene.add(quad);
  }

  setSize(w: number, h: number): void {
    (this.mat.uniforms.uRes.value as Vector2).set(w, h);
  }
}
